import { useState } from "react";
import { Loader2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AreaContentFields from "@/components/admin/AreaContentFields";
import { areaContentOf, catalogContentFor, contentFromDraft, contentToRow, draftFromContent, type Area } from "@/lib/areas";
import { upsertAreaRow } from "@/lib/areasApi";
import { edgeErrorMessage } from "@/lib/edgeFunctionError";

interface Props {
  area: Area;
  /** Called after a successful save; the caller refetches the areas. */
  onSaved: () => void;
  onCancel: () => void;
}

/** In-page editor (admins only) for one area's prose. Writes public.areas. */
const AreaContentEditor = ({ area, onSaved, onCancel }: Props) => {
  const [draft, setDraft] = useState(() => draftFromContent(areaContentOf(area)));
  const [saving, setSaving] = useState(false);
  const catalogDefault = catalogContentFor(area.slug);

  const save = async (content = contentFromDraft(draft)) => {
    setSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      await upsertAreaRow({
        tenant_id: area.tenantId,
        slug: area.slug,
        name: area.name,
        updated_by: userData?.user?.id ?? null,
        ...contentToRow(content),
      });
      toast.success("Area content updated");
      onSaved();
    } catch (e) {
      toast.error(`Could not save: ${await edgeErrorMessage(e, null)}`);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!catalogDefault) return;
    if (!confirm(`Reset ${area.name} to the built-in text? Your edits will be replaced.`)) return;
    await save(catalogDefault);
  };

  return (
    <div className="bg-card border-2 border-primary/30 rounded-2xl p-5 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-lg font-semibold">Edit area content</h3>
        <Button variant="ghost" size="icon" onClick={onCancel} disabled={saving} aria-label="Close editor">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <AreaContentFields value={draft} onChange={setDraft} />

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
        {catalogDefault ? (
          <Button variant="ghost" size="sm" className="text-destructive" onClick={resetToDefault} disabled={saving}>
            Reset to built-in text
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">No built-in text for this area.</span>
        )}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={() => save()} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AreaContentEditor;
